import { useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';
import RequestAccessModal from '../../components/billing/RequestAccessModal';

export default function GitHubWorkflowDriftDetectionPage() {
  const [showRequestAccess, setShowRequestAccess] = useState(false);

  const faqs = [
    {
      question: 'What is workflow drift in GitHub Actions?',
      answer: 'Workflow drift occurs when GitHub Actions workflow files or required status checks are modified, potentially weakening automated controls that enforce testing, security scanning, or approval requirements.'
    },
    {
      question: 'How often does drift detection run?',
      answer: 'Drift detection runs on a scheduled basis (typically hourly) for enabled repositories. Check frequency varies by plan tier.'
    },
    {
      question: 'What happens when drift is detected?',
      answer: 'When drift is detected, EvidentTrail creates a drift change record showing what changed, who made the change, and when. This provides an audit trail for control modifications.'
    },
    {
      question: 'Can I acknowledge expected drift changes?',
      answer: 'Yes. Drift changes can be acknowledged when they represent intentional, authorized modifications (e.g., adding new required checks, updating test configurations).'
    }
  ];

  const longformSections = [
    {
      title: 'What auditors ask for',
      paragraphs: [
        'Auditors need evidence that automated controls (CI checks, required status checks) operated consistently throughout the audit period. Workflow drift detection provides a change log for these enforcement mechanisms.'
      ],
      bullets: [
        'Timeline of changes to required status checks',
        'Evidence that CI workflows were not disabled or weakened',
        'Who authorized changes to automated controls',
        'Duration of any weakened enforcement periods',
        'Acknowledgement records for intentional control modifications'
      ]
    },
    {
      title: 'Evidence artifacts included',
      paragraphs: [
        'Drift detection captures snapshots of GitHub Actions workflow configurations and required status checks, comparing them over time to identify modifications.'
      ],
      bullets: [
        'Workflow file changes (.github/workflows/*.yml)',
        'Required status check modifications',
        'Required check enforcement settings',
        'Workflow run requirements (e.g., approval requirements)',
        'Timestamps and author identity for each change'
      ]
    },
    {
      title: 'How EvidentTrail works (drift detection)',
      paragraphs: [
        'EvidentTrail periodically scans workflow files and repository settings, comparing current state to previous snapshots. Changes are flagged as drift and recorded for audit review.'
      ],
      bullets: [
        'Scheduled scans (typically hourly for enabled repos)',
        'Snapshot comparison to identify changes',
        'Drift change records with before/after states',
        'Acknowledgement workflow for expected changes',
        'Usage metering and quotas vary by plan tier'
      ]
    },
    {
      title: 'How to interpret the output',
      paragraphs: [
        'Review drift change records to identify when automated controls were modified. Acknowledged changes represent intentional modifications; unacknowledged changes may indicate unauthorized weakening of controls.'
      ],
      bullets: [
        'Check drift timeline for changes during audit window',
        'Verify modifications were authorized and acknowledged',
        'Review before/after states to assess control impact',
        'Cross-reference with exception records if controls were bypassed',
        'Export drift history for auditor review'
      ]
    },
    {
      title: 'Common failure modes',
      paragraphs: [
        'Drift detection can fail if repositories are not enabled for monitoring, if drift check quotas are exceeded, or if workflow files are deleted entirely rather than modified.'
      ],
      bullets: [
        'Repository not enabled for drift detection',
        'Monthly drift check quota exceeded',
        'Workflow files deleted (no before state to compare)',
        'Required check names changed (appears as removed + added)',
        'Drift changes not acknowledged, creating false audit findings'
      ]
    }
  ];

  return (
    <>
      <MarketingLayout
        title="GitHub Workflow Drift Detection for Compliance | EvidentTrail"
        description="Monitor changes to GitHub Actions workflows and required status checks. Detect when automated controls are modified or weakened during audit periods."
        h1="GitHub Workflow Drift Detection"
        heroText="Detect when GitHub Actions workflows and required checks are modified, creating an audit trail for automated control changes."
        type="article"
      >
        <BreadcrumbJsonLd
          items={[
            { name: 'Home', url: 'https://evidenttrail.com/' },
            { name: 'GitHub Workflow Drift Detection', url: 'https://evidenttrail.com/github-workflow-drift-detection' }
          ]}
        />
        <FaqJsonLd items={faqs} />

        {/* Drift Detection Explained */}
        <section className={tokens.sectionAlt}>
          <div className={tokens.container}>
            <div className="max-w-3xl mx-auto">
              <h2 className={`${tokens.heading.h2} mb-6`}>What Workflow Drift Detection Monitors</h2>
              <p className={`${tokens.text.base} mb-6`}>
                Workflow drift detection tracks changes to GitHub Actions workflow files and required status checks, providing evidence that automated controls operated consistently during audit periods.
              </p>

              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-4`}>Monitored Elements</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Required Status Checks:</strong> Which CI checks must pass before merge (e.g., test-suite, lint, security-scan)
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Workflow Files:</strong> Changes to .github/workflows/*.yml that define CI/CD automation
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Enforcement Settings:</strong> Whether required checks are strictly enforced or can be bypassed by admins
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Workflow Requirements:</strong> Approval requirements or environment protection rules for deployment workflows
                    </span>
                  </li>
                </ul>
              </div>

              <h3 className={`${tokens.heading.h3} mb-4 mt-8`}>Example: Drift Detection Scenario</h3>
              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div className="border border-green-500/30 bg-green-950/20 rounded-lg p-6">
                  <h4 className="text-green-300 font-semibold mb-3">Before (Jan 15)</h4>
                  <div className="font-mono text-xs text-slate-300 space-y-1">
                    <div><strong>Required Checks:</strong></div>
                    <div>• test-suite</div>
                    <div>• lint</div>
                    <div>• security-scan</div>
                    <div className="mt-2"><strong>Enforcement:</strong> strict</div>
                  </div>
                </div>
                <div className="border border-amber-500/30 bg-amber-950/20 rounded-lg p-6">
                  <h4 className="text-amber-300 font-semibold mb-3">After (Jan 20)</h4>
                  <div className="font-mono text-xs text-slate-300 space-y-1">
                    <div><strong>Required Checks:</strong></div>
                    <div>• test-suite</div>
                    <div>• lint</div>
                    <div className="mt-2"><strong>Enforcement:</strong> strict</div>
                    <div className="mt-2 text-amber-300">⚠️ security-scan removed</div>
                  </div>
                </div>
              </div>
              <p className={`${tokens.text.small} text-slate-400 italic`}>
                Drift detected: security-scan removed from required checks. Auditor would review changes merged between Jan 15-20 to determine if security scanning was bypassed.
              </p>
            </div>
          </div>
        </section>

        <SeoLongformSections sections={longformSections} faqs={faqs} />

        {/* Internal Linking */}
        <section className={tokens.section}>
          <div className={tokens.container}>
            <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Drift Detection Topics</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/branch-protection-drift-detection" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Branch Protection Drift Detection</h4>
                <p className="text-sm text-slate-400">Track changes to branch protection rules and enforcement settings.</p>
              </Link>
              <Link to="/ci-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">CI/CD Evidence</h4>
                <p className="text-sm text-slate-400">Link CI check results to change control.</p>
              </Link>
              <Link to="/exception-tracking" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Exception Tracking</h4>
                <p className="text-sm text-slate-400">Document control bypasses with formal exceptions.</p>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={tokens.sectionAlt}>
          <div className={tokens.container}>
            <div className="max-w-2xl mx-auto text-center">
              <h2 className={`${tokens.heading.h2} mb-4`}>Try Workflow Drift Detection</h2>
              <p className={`${tokens.text.base} mb-8`}>
                Request access to start monitoring workflow and required check changes for compliance.
              </p>
              <button 
                onClick={() => setShowRequestAccess(true)}
                className="btn btn-primary text-lg px-8 py-3"
              >
                Request Access
              </button>
            </div>
          </div>
        </section>
      </MarketingLayout>

      <RequestAccessModal 
        isOpen={showRequestAccess}
        onClose={() => setShowRequestAccess(false)}
        preselectedFeature="Workflow Drift Detection"
      />
    </>
  );
}
